import { EventService, EventServiceConfig } from '@foxcorp/foxkit-event-js/event-service';
import { getCookie } from 'cookies-next/client';

import { getApiConfig } from '@/api/config/client/getApiConfig';
import { getUiConfig } from '@/config/client/getUiConfig';
import { getHeapInstance } from '@/features/analytics/heap/useHeap';
import { getXid } from '@/features/auth/client';
import { computeEventServiceAccountId } from '@/sdk/computeEventServiceAccountId';

type Env = 'dev' | 'stage' | 'production';

const mapEnvToSDKUrl: Record<Env, Foxkit.EventService.Url> = {
  dev: EventService.Urls.DevFox,
  stage: EventService.Urls.StageFox,
  production: EventService.Urls.ProdFox,
};

let eventServiceInitPromise: Promise<EventService> | null = null;

declare global {
  interface Window {
    __eventService?: EventService;
    __lastEventServiceAccountIdApplied?: string;
  }
}

export async function getEventServiceSDK(): Promise<EventService> {
  if (typeof window === 'undefined') {
    throw new Error('getEventServiceSDK() must be called on the client');
  }

  if (window.__eventService) {
    return window.__eventService;
  }

  if (eventServiceInitPromise) {
    return eventServiceInitPromise;
  }

  eventServiceInitPromise = (async () => {
    const heap = await getHeapInstance();

    const usPrivacy = getCookie('us_privacy') || '';
    const xid = await getXid();

    const accountId = computeEventServiceAccountId();

    // @ts-expect-error: Heap typings did not declare this, if heap is not availble sdk will auto fill with event session id when ''
    const heapSessionId = heap ? (window.heap.getSessionId?.() ?? '') : '';

    const apiConfig = getApiConfig();
    const uiConfig = getUiConfig();

    const config = new EventServiceConfig({
      accountId,
      appBuild: uiConfig.appVersion,
      appNamespace: 'FOX One Web',
      apiKey: apiConfig.apiKey,
      appName: uiConfig.appName,
      appVersion: uiConfig.appVersion,
      locale: 'en-US',
      lastXid: xid,
      xid,
      usPrivacy,
      heapAppId: uiConfig.heapAppId,
      heapSessionId,
      url: mapEnvToSDKUrl[uiConfig.environment],
    });

    window.__eventService = new EventService(config);
    window.__lastEventServiceAccountIdApplied = accountId;

    return window.__eventService;
  })();

  return eventServiceInitPromise;
}

export async function syncEventServiceAccountId(): Promise<void> {
  const es = await getEventServiceSDK();
  const nextId = computeEventServiceAccountId();

  if (window.__lastEventServiceAccountIdApplied !== nextId) {
    es.updateAccountId(nextId);
    window.__lastEventServiceAccountIdApplied = nextId;
  }
}
